import type { RepositoryResponse, RepositoryResponseDetailed } from '@emma/api_client/types';
import { Box, Checkbox, CheckboxGroup, HorizontalStack, Stack, Text } from '@lego/klik-ui';
import { useState } from 'react';
import type { StoreService } from '@/hooks/store';
import { Loader, TextHeader } from '../common';
import { AppWizardDropdown } from './AppWizardDropdown';

type BaseServiceProps = {
  title: string;
  onSelectedServicesChange: (selectedService: RepositoryResponseDetailed | StoreService) => void;
  onUnselectChange: (serviceName: string | number) => void;
};

type WorkspaceServiceProps = BaseServiceProps & {
  workspaceId: number;
  services: RepositoryResponse[] | undefined;
  type: 'WorkspaceServices';
};

type StoreServiceProps = BaseServiceProps & {
  services: StoreService[] | undefined;
  type: 'StoreServices';
};

type ServiceProps = WorkspaceServiceProps | StoreServiceProps;

export const AppWizardServices = (props: ServiceProps) => {
  const [checkedServices, setCheckedServices] = useState<(RepositoryResponse | StoreService)[]>([]);

  const handleCheckboxChange = (service: RepositoryResponse | StoreService) => {
    setCheckedServices(
      checkedServices.includes(service)
        ? checkedServices.filter((value) => value !== service)
        : [...checkedServices, service]
    );
    props.onUnselectChange('imageName' in service ? service.imageName : service.id);
  };

  if (!props.services) return <Loader />;
  if (props.services.length === 0) return null;

  return (
    <Box
      background="white"
      boxShadow={'md'}
      padding={6}
      rounded={'md'}
      width={{ base: '100%', lg: '500px' }}
    >
      <TextHeader mb={2} tagLevel="h4" title={props.title} />
      <CheckboxGroup>
        <Stack spacing={5}>
          {props.services.map((service) => (
            <Checkbox
              height={8}
              isChecked={checkedServices.includes(service)}
              key={service.name}
              onChange={() => handleCheckboxChange(service)}
              size={'md'}
              width={'full'}
            >
              <HorizontalStack spacing={4}>
                <Text>{service.name}</Text>
                <Box>
                  {checkedServices.includes(service) &&
                    (props.type === 'WorkspaceServices' ? (
                      <AppWizardDropdown
                        onSelectedServicesChange={props.onSelectedServicesChange}
                        repositoryId={(service as RepositoryResponse).id}
                        type="WorkspaceService"
                        workspaceId={props.workspaceId}
                      />
                    ) : (
                      <AppWizardDropdown
                        onSelectedServicesChange={props.onSelectedServicesChange}
                        service={service as StoreService}
                        type="StoreService"
                      />
                    ))}
                </Box>
              </HorizontalStack>
            </Checkbox>
          ))}
        </Stack>
      </CheckboxGroup>
    </Box>
  );
};
